import {
	Column,
	isIconName,
	Icon,
	Text,
	type StylableProps,
	type IconName,
	CollapsibleBase,
} from "@mdotm/shadcn/legacy/components";
import { DropdownMenu, type DropdownTriggerProps, type DropdownMenuProps } from "@mdotm/shadcn/mdotm/components";
import { overrideClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { ForEach } from "@mdotm/utils/react-extra";
import { Sandwich } from "@mdotm/shadcn/mdotm/layout";
import { type ReactNode, useEffect } from "react";
import type Immutable from "immutable";
import { filterMap } from "$root/utils";
import { type RwAtom, useAtom } from "@mdotm/shadcn/legacy/reactivity";

function ItemActionsTrigger({ state, ...props }: DropdownTriggerProps) {
	return (
		<button
			type="button"
			className={overrideClassName(buttonClassName, "absolute inset-y-1.5 right-1.5 p-1")}
			{...props}
		>
			<Icon
				icon={state === "loading" ? "calulating" : "More"}
				size={16}
				className={{ "animate-spin": state === "loading" }}
			/>
		</button>
	);
}

export function HierarchicalTreeModule({
	items,
	level = 0,
	expandMap$,
	className,
	style,
}: { items: AppDrawerTreeNode[]; level?: number; expandMap$: RwAtom<Immutable.Map<string, boolean>> } & StylableProps) {
	const [expandMap, setExpandMap] = useAtom(expandMap$);
	useEffect(() => {
		setExpandMap((m) =>
			m.merge(filterMap(items, (x) => (x.active && hasChildren(x) ? [[`${level}-${x.name}`, true]] : []))),
		);
	}, [items, level, setExpandMap]);

	return (
		<Column flexGrow={0} gap={12} style={style} className={className}>
			<ForEach collection={items} keyProvider={(item) => item.name}>
				{(item) => {
					const Tag = "href" in item ? "a" : "button";
					return (
						<Column flexGrow={0} gap={12}>
							<div
								className="group flex w-full justify-items-stretch"
								style={{
									paddingLeft: level * 16,
								}}
							>
								<Sandwich className="flex min-w-0 grow" data-qualifier={item.qualifier}>
									<Tag
										className="peer relative flex min-w-0 grow items-center gap-2 py-2 transition-colors rounded-sm pl-2 pr-3 text-(--palette-N600) group-hover:bg-(--palette-N100) group-hover:aria-current:bg-(--palette-N600) aria-current:bg-(--palette-N500) aria-current:text-(--palette-N0)"
										aria-current={item.active && (!hasChildren(item) || !item.children.some((c) => c.active))}
										href={item.href}
									>
										{item.icon == null || (typeof item.icon === "string" && isIconName(item.icon)) ? (
											<Icon
												className={{
													"relative pointer-events-none opacity-100": true,
													"group-hover:opacity-0": hasChildren(item),
												}}
												icon={
													item.icon ??
													(hasChildren(item) ? (expandMap.get(item.name) ? "folderopen" : "folder") : "workspace")
												}
												size={20}
											/>
										) : (
											item.icon
										)}
										<Text type="Body/M/Medium" color="inherit" className="truncate" title={item.title}>
											{item.title}
										</Text>
										{item.count != null && (
											<Text
												type="Body/M/Book"
												color="inherit"
												className="ml-auto shrink-0 pointer-events-none opacity-100 group-hover:opacity-0"
												title={item.title}
											>
												{item.count}
											</Text>
										)}
									</Tag>

									{hasChildren(item) && (
										<button
											data-qualifier={`${item.qualifier}/Expand`}
											aria-expanded={expandMap.get(item.name) ?? false}
											type="button"
											className={overrideClassName(buttonClassName, "absolute inset-y-1.5 left-1.5 p-1")}
											onClick={() => setExpandMap((m) => m.set(item.name, !m.get(item.name)))}
										>
											<Icon
												icon="Down"
												size={16}
												className={{
													"transition-transform": true,
													"-rotate-90": !expandMap.get(item.name),
												}}
											/>
										</button>
									)}
									{item.actions && item.actions.length > 0 && (
										<DropdownMenu
											actions={item.actions}
											Trigger={ItemActionsTrigger}
											extraTriggerProps={{ "data-qualifier": `${item.qualifier}/Actions` }}
										/>
									)}
								</Sandwich>
							</div>
							{hasChildren(item) && (
								<CollapsibleBase unmountOnExit expand={expandMap.get(item.name) ?? false}>
									<HierarchicalTreeModule items={item.children} level={level + 1} expandMap$={expandMap$} />
								</CollapsibleBase>
							)}
						</Column>
					);
				}}
			</ForEach>
		</Column>
	);
}

export type AppDrawerTreeNode<Name extends string = string> = {
	active: boolean;
	name: Name;
	title: string;
	qualifier?: string;
	href?: string;
	count?: number;
	actions?: DropdownMenuProps["actions"];
	/** @default "folder" | "folderopen" | "workspace" */
	icon?: IconName | (string & {}) | Exclude<ReactNode, string>;
	children?: AppDrawerTreeNode[];
};

function hasChildren(item: AppDrawerTreeNode): item is AppDrawerTreeNode & { children: AppDrawerTreeNode[] } {
	return "children" in item && item.children != null && item.children.length > 0;
}

const buttonClassName = [
	"block opacity-0 transition-colors rounded-sm",
	"peer-aria-current:aria-expanded:bg-(--palette-N500)",
	"peer-aria-current:aria-expanded:group-hover:bg-(--palette-N600)",
	"peer-aria-current:aria-expanded:hover:bg-(--palette-N700)",
	"peer-aria-current:group-hover:bg-(--palette-N600)",
	"peer-aria-current:hover:bg-(--palette-N700)",
	"peer-aria-current:text-(--palette-N0)",

	"aria-expanded:bg-(--palette-N0)",
	"aria-expanded:group-hover:bg-(--palette-N100)",
	"aria-expanded:hover:bg-(--palette-N200)",
	"group-hover:bg-(--palette-N100)",
	"hover:bg-(--palette-N200)",
	"text-(--palette-N1000)",

	"aria-expanded:opacity-100",
	"group-hover:opacity-100",
].join(" ");
